import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Plus, Package, Users } from "lucide-react";
import { useProductFamily } from "@/hooks/useProductFamily";
import { AddFamilyProductDialog } from "./AddFamilyProductDialog";

interface ProductFamilyCardProps {
  productId: string;
  productName: string;
  barcode: string;
  showAddButton?: boolean;
}

export const ProductFamilyCard = ({ 
  productId, 
  productName, 
  barcode,
  showAddButton = true 
}: ProductFamilyCardProps) => {
  const { familyMembers, isLoading, addToFamily } = useProductFamily(productId);
  const [showAddDialog, setShowAddDialog] = useState(false);

  if (isLoading) {
    return (
      <Card>
        <CardContent className="p-4">
          <div className="animate-pulse">Loading family products...</div>
        </CardContent>
      </Card>
    );
  }

  const isStandaloneProduct = familyMembers.length === 1 && 
    !familyMembers[0].is_parent && 
    !familyMembers[0].parent_id;

  const parentProduct = familyMembers.find(member => member.is_parent);
  const childProducts = familyMembers.filter(member => !member.is_parent && member.parent_id);
  const hasFamily = familyMembers.length > 1;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center justify-between text-base">
          <div className="flex items-center gap-2">
            <Users className="h-4 w-4" />
            Product Family
          </div>
          {showAddButton && (
            <Button 
              size="sm" 
              variant="outline" 
              onClick={() => setShowAddDialog(true)}
              className="gap-1"
            >
              <Plus className="h-3 w-3" />
              Add Size
            </Button>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent className="pt-0">
        {isStandaloneProduct ? (
          <div className="text-center py-4">
            <Package className="h-8 w-8 mx-auto text-muted-foreground mb-2" />
            <p className="text-sm text-muted-foreground">
              No related products yet
            </p>
            <p className="text-xs text-muted-foreground">
              Add different sizes or variants of this product
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            {/* Parent Product */}
            {parentProduct && (
              <div className="p-3 bg-muted rounded-md">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium text-sm">{parentProduct.product_name}</p>
                    <p className="text-xs text-muted-foreground">{parentProduct.barcode}</p>
                  </div>
                  <Badge variant="secondary" className="text-xs">Parent</Badge>
                </div>
              </div>
            )}

            {/* Child Products */}
            {childProducts.map((child) => (
              <div key={child.id} className="p-3 border rounded-md">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium text-sm">{child.product_name}</p>
                    <p className="text-xs text-muted-foreground">{child.barcode}</p>
                    {child.size_variant && (
                      <Badge variant="outline" className="text-xs mt-1">
                        {child.size_variant}
                      </Badge>
                    )}
                  </div>
                  <div className="text-right">
                    <p className="text-sm font-medium">
                      {child.retail_price ? `$${child.retail_price.toFixed(2)}` : 'N/A'}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>

      {showAddDialog && (
        <AddFamilyProductDialog
          parentProductId={parentProduct?.id || productId}
          open={showAddDialog}
          onOpenChange={setShowAddDialog}
          onAdd={addToFamily}
        />
      )}
    </Card>
  );
};